import { isAxiosError } from "axios";
import { format } from "date-fns";
import i18n, { t } from "i18next";
import axios from "../lib/axios";
import { AnnualLeaveLimitEntry, CEID } from "../types";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AbsenceType2 {
  id: number;
  name: string;
  type: number;
  rozliczDni: number;
  // Spółki, w których słowniku Optimy ten typ istnieje. Słowniki spółek się różnią,
  // a nieobecność zapisywana jest do Optimy spółki PRACOWNIKA - patrz filtrowanie
  // w AbsenceCreatePopup2 po spółkach wybranych pracowników.
  companyIds: number[];
}

export interface AbsenceUserType2 {
  id: number;
  name: string;
  rozliczDni: number;
}

export interface AbsenceRecord2 {
  id: number;
  employeeId: number;
  fromDate: string;
  toDate: string;
  name: string;
  skipDaysOff: boolean;
  leaveForAssignment: number;
  note: string | null;
  confirmed: number;
  transferred: number;
  synch: boolean;
  type: number | null;
}

export interface GroupedAbsences2 {
  employeeId: number;
  companyId: number;
  absences: AbsenceRecord2[];
}

export interface PendingAbsence2 {
  id: number;
  employeeId: number;
  companyId: number;
  from: string;
  to: string;
  name: string;
  note: string | null;
  firstName: string | null;
  lastName: string | null;
  department: string | null;
}

export interface SubordinateEmployee2 {
  id: number;
  companyId: number;
  contractType: number;
  code: string;
  firstName: string;
  lastName: string;
  isBorrowed: boolean;
  borrowedByManagerId?: number | null;
  borrowedByManagerCompanyId?: number | null;
  startDateOfTheRental?: string | null;
  endDateOfTheRental?: string | null;
}

export interface HolidayLimit2 {
  remainingAnnualLeaveDays: number;
  remainingAnnualLeaveHours: number;
  unconfirmedAnnualLeaveDays: number;
  unconfirmedAnnualLeaveHours: number;
}

export interface AbsenceCreateRequest2 {
  reportingEmployeeId: number;
  reportingCompanyId: number;
  employees: CEID[];
  from: string;
  to: string;
  name: string;
  skipDaysOff: boolean;
  leaveForAssignment?: number;
  confirmed: number;
  note?: string;
  type?: number;
  hourFrom?: string | null;
  hourTo?: string | null;
}

interface ApiResult<T> {
  isOk: boolean;
  data?: T;
  error?: string;
}

// ─── Generic error handler ───────────────────────────────────────────────────

function extractErrorMessage(error: unknown): string {
  let message = t("common.unknownErrorOccurred");

  if (isAxiosError(error)) {
    if (error.response) {
      if (error.response.status === 403) {
        message = t("noPermissions");
      } else if (error.response.data) {
        const data = error.response.data;
        if (
          "errors" in data &&
          Array.isArray(data.errors) &&
          data.errors.length > 0 &&
          typeof data.errors[0] === "object" &&
          "message" in data.errors[0]
        ) {
          message = data.errors[0].message;
        } else if ("message" in data) {
          message = data.message;
        }
      }
    } else if (error.request) {
      message = t("common.noResponseFromServer");
    }
  }

  return message;
}

async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<ApiResult<T>> {
  try {
    const { data } = await axios.get<T>(url, {
      params,
      headers: { "Accept-Language": i18n.language },
    });
    return { isOk: true, data };
  } catch (error) {
    return { isOk: false, error: extractErrorMessage(error) };
  }
}

async function apiPost<T>(url: string, body?: unknown, params?: Record<string, unknown>): Promise<ApiResult<T>> {
  try {
    const { data } = await axios.post<T>(url, body, {
      params,
      headers: { "Accept-Language": i18n.language },
    });
    return { isOk: true, data };
  } catch (error) {
    return { isOk: false, error: extractErrorMessage(error) };
  }
}

async function apiPut<T>(url: string, body?: unknown, params?: Record<string, unknown>): Promise<ApiResult<T>> {
  try {
    const { data } = await axios.put<T>(url, body, {
      params,
      headers: { "Accept-Language": i18n.language },
    });
    return { isOk: true, data };
  } catch (error) {
    return { isOk: false, error: extractErrorMessage(error) };
  }
}

async function apiDelete<T>(url: string, params?: Record<string, unknown>): Promise<ApiResult<T>> {
  try {
    const { data } = await axios.delete<T>(url, {
      params,
      headers: { "Accept-Language": i18n.language },
    });
    return { isOk: true, data };
  } catch (error) {
    return { isOk: false, error: extractErrorMessage(error) };
  }
}

// ─── API functions ───────────────────────────────────────────────────────────

export function getAbsenceTypes2() {
  return apiGet<AbsenceType2[]>("/api/v2/absences/types");
}

export function getAbsenceTypesForUser2() {
  return apiGet<AbsenceUserType2[]>("/api/v2/absences/types/user");
}

export function getAbsences2(year: number, month: number, ceids: CEID[]) {
  return apiPost<GroupedAbsences2[]>("/api/v2/absences", ceids, { year, month });
}

export function createAbsence2(request: AbsenceCreateRequest2) {
  return apiPost<{ message?: string; warning?: string }>("/api/v2/absences/create", request);
}

export function confirmAbsence2(id: number) {
  return apiPost<void>(`/api/v2/absences/${id}/confirm`);
}

export function rejectAbsence2(id: number) {
  return apiPut<void>(`/api/v2/absences/${id}/reject`);
}

export async function deleteOwnAbsence2(
  id: number,
): Promise<string | undefined> {
  const result = await apiDelete<{ warning?: string } | null>(
    `/api/v2/absences/${id}`,
  );
  if (!result.isOk) {
    throw new Error(result.error);
  }
  return result.data?.warning ?? undefined;
}

export async function cancelAbsence2(id: number): Promise<void> {
  try {
    await axios.post(`/api/v2/absences/${id}/cancel`, undefined, {
      headers: { "Accept-Language": i18n.language },
    });
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
}

export function getPendingAbsences2(companyId: number, year?: number, month?: number) {
  return apiGet<PendingAbsence2[]>("/api/v2/absences/pending", { companyId, year, month });
}

export async function getHolidayLimit2(year?: number, month?: number): Promise<HolidayLimit2> {
  const result = await apiGet<HolidayLimit2>("/api/v2/absences/limit", { year, month });
  if (!result.isOk || !result.data) {
    throw new Error(result.error);
  }
  return result.data;
}

export async function getAnnualLeaveLimits2(
  year: number,
  month: number,
  ceids: CEID[],
): Promise<AnnualLeaveLimitEntry[]> {
  const result = await apiPost<AnnualLeaveLimitEntry[]>("/api/v2/absences/limits", ceids, { year, month });
  if (!result.isOk || !result.data) {
    throw new Error(result.error);
  }
  return result.data;
}

export function getSubordinates2(companyId?: number, from?: string, to?: string) {
  return apiGet<SubordinateEmployee2[]>("/api/v2/subordinates", { companyId, from, to });
}

// Re-export work plan functions from existing API (they are not part of absence module)
export { fetchWorkPlan, fetchWorkPlanForManager } from "./calendarData";
export type { WorkPlan, AnnualLeaveEntry, PagedWorkPlanResult } from "./calendarData";

// Download calendar data for employee view
export function downloadCalendarData2(year?: number, month?: number) {
  return apiGet<{
    date: string;
    dayType: number | null;
    hours: { fromHour: string; toHour: string; duration: string; note: string; project: string }[];
  }[]>("/api/work-plan-for-authenticated-user", { year, month });
}

// Send absence with proper date formatting
export function sendAbsence2(
  reportingEmployeeId: number,
  reportingCompanyId: number,
  from: Date,
  to: Date,
  name: string,
  skipDaysOff: boolean,
  leaveForAssignment: number,
  note: string,
  employees: CEID[],
  type?: number,
  hourFrom?: string | null,
  hourTo?: string | null,
) {
  return createAbsence2({
    reportingEmployeeId,
    reportingCompanyId,
    from: format(from, "yyyy-MM-dd'T'00:00:00"),
    to: format(to, "yyyy-MM-dd'T'00:00:00"),
    name,
    skipDaysOff,
    leaveForAssignment,
    confirmed: 0,
    note,
    employees,
    type,
    hourFrom,
    hourTo,
  });
}
